import React, { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Archive, Loader2, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Event {
  id: string;
  region: string;
  conference_year?: number | null;
  status?: string;
  start_date?: string | null;
  created_at?: string;
}

interface Props {
  events: Event[];
  selectedYear: number | null;
  onYearChange: (year: number) => void;
  region?: string;
  isGlobalAdmin: boolean;
  onArchived: () => void;
}

const yearOf = (e: Event) =>
  e.conference_year ?? (e.start_date ? new Date(e.start_date).getFullYear() : new Date(e.created_at || Date.now()).getFullYear());

export const YearArchiveBar: React.FC<Props> = ({ events, selectedYear, onYearChange, region, isGlobalAdmin, onArchived }) => {
  const years = useMemo(() => {
    const set = new Set<number>();
    events.forEach((e) => set.add(yearOf(e)));
    const arr = Array.from(set).sort((a, b) => b - a);
    if (!arr.length) arr.push(new Date().getFullYear());
    return arr;
  }, [events]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (selectedYear === null && years.length) onYearChange(years[0]);
  }, [years, selectedYear, onYearChange]);

  const yearStatus = useMemo(() => {
    if (selectedYear === null) return null;
    const yearEvents = events.filter((e) => yearOf(e) === selectedYear && (!region || e.region === region));
    if (!yearEvents.length) return null;
    const allArchived = yearEvents.every((e) => e.status === "archived");
    return allArchived ? "archived" : "active";
  }, [events, selectedYear, region]);

  const archive = async () => {
    if (selectedYear === null) return;
    setArchiving(true);
    const { data, error } = await (supabase as any).rpc("archive_conference_year", {
      p_year: selectedYear,
      p_region: region || null,
    });
    setArchiving(false);
    if (error) {
      toast({ title: "Archive failed", description: error.message, variant: "destructive" });
      return;
    }
    const result = Array.isArray(data) ? data[0] : data;
    toast({
      title: `Archived ${selectedYear}`,
      description: `${result?.archived_count ?? 0} conference(s) archived, ${result?.created_count ?? 0} new ${selectedYear + 1} shell(s) created.`,
    });
    setConfirmOpen(false);
    onArchived();
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1.5 text-sm">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-muted-foreground">Year:</span>
      </div>
      <Select value={String(selectedYear ?? "")} onValueChange={(v) => onYearChange(Number(v))}>
        <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {yearStatus === "archived" && (
        <Badge variant="secondary">Archived (read-only)</Badge>
      )}
      {isGlobalAdmin && yearStatus === "active" && selectedYear !== null && (
        <Button size="sm" variant="outline" onClick={() => setConfirmOpen(true)}>
          <Archive className="w-4 h-4 mr-1" /> Archive {selectedYear} & start {selectedYear + 1}
        </Button>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {selectedYear}?</DialogTitle>
            <DialogDescription>
              This marks all {region ? `${region} ` : ""}{selectedYear} conference kit(s) as <strong>archived</strong> (read-only with full history)
              and creates an empty planning shell for {selectedYear !== null ? selectedYear + 1 : ""}. This cannot be undone from the UI.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={archiving}>Cancel</Button>
            <Button onClick={archive} disabled={archiving}>
              {archiving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Archive className="w-4 h-4 mr-1" />}
              Archive & Start New Year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
